import { API } from './config';

let eventSource = null;
let reconnectTimeout = null;
let stopped = true;
let retryDelay = 5000;

// EventSource can't see HTTP status codes, so after an error we ask the
// verify endpoint whether the token is still good before reconnecting.
// A dead token stops the loop and tells the app to show the login screen.
async function tokenStillValid(token) {
  try {
    const res = await fetch(`${API}/api/auth/verify`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) return false;
    return true; // 2xx, 403, 5xx → keep trying (server hiccup, not a dead login)
  } catch (e) {
    return true; // offline — retry later
  }
}

export function connectWebSocket(organizationId) {
  stopped = false;
  if (eventSource) return;

  // Backend requires a valid token on the SSE stream (EventSource can't
  // send headers, so it travels as a query param)
  const token = localStorage.getItem('token');
  if (!token) return;
  // A 60-second ticket goes in the URL, not the 7-day login token (URLs end
  // up in proxy logs). Falls back to the token if the ticket route is missing.
  eventSource = { close() {} }; // placeholder so a second call doesn't open twice
  fetch(`${API}/api/events/ticket`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => (r.ok ? r.json() : null))
    .catch(() => null)
    .then((t) => {
      if (stopped) { eventSource = null; return; }
      const url = t && t.ticket ? `${API}/api/events?ticket=${encodeURIComponent(t.ticket)}` : `${API}/api/events?token=${encodeURIComponent(token)}`;
      openStream(url, organizationId);
    });
}

function openStream(url, organizationId) {
  eventSource = new EventSource(url);

  eventSource.onopen = () => {
    retryDelay = 5000;
  };

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type !== 'connected') {
        window.dispatchEvent(new CustomEvent('ws-event', { detail: data }));
      }
    } catch (e) {}
  };

  eventSource.onerror = async () => {
    if (eventSource) eventSource.close();
    eventSource = null;
    if (stopped) return;
    const current = localStorage.getItem('token');
    if (!current) { stopped = true; return; }
    const ok = await tokenStillValid(current);
    if (stopped) return;
    if (!ok) {
      stopped = true;
      window.dispatchEvent(new CustomEvent('auth-expired'));
      return;
    }
    if (reconnectTimeout) clearTimeout(reconnectTimeout);
    reconnectTimeout = setTimeout(() => connectWebSocket(organizationId), retryDelay);
    retryDelay = Math.min(retryDelay * 2, 60000);
  };
}

export function disconnectWebSocket() {
  stopped = true;
  if (reconnectTimeout) { clearTimeout(reconnectTimeout); reconnectTimeout = null; }
  if (eventSource) { eventSource.close(); eventSource = null; }
}
